import type { ProductCategory, PricingStrategy } from './pricing';

export interface ProductImage {
  id: number;
  imageUrl: string;
  /** Full-resolution original; falls back to imageUrl when absent. */
  originalImageUrl?: string;
}

/** Prefer original when present (detail / lightbox); otherwise preview. */
export function productImageSrc(image: ProductImage, quality: 'preview' | 'original' = 'preview'): string {
  if (quality === 'original') {
    return image.originalImageUrl || image.imageUrl;
  }
  return image.imageUrl;
}

export type { ProductCategory } from './pricing';

export interface Product {
  id: number;
  name: string;
  description: string;
  category: ProductCategory;
  estimatedCurrentPrice: number;
  originalPrice?: number | null;
  ageYears?: number | null;
  renterFixedFee: number;
  renterFeePerDay: number;
  pricingStrategy?: PricingStrategy;
  price: number;
  images?: ProductImage[];
  createdById?: number;
  createdByUsername?: string;
  lenderApproximateLatitude?: number;
  lenderApproximateLongitude?: number;
  lenderAverageRating?: number;
  lenderRatingCount?: number;
  lenderLevel?: number;
  isFeatured?: boolean;
}

export interface PublicUser {
  id: number;
  username: string;
  level: number;
  averageRating: number;
  ratingCount: number;
  isVerified: boolean;
  approximateLatitude?: number | null;
  approximateLongitude?: number | null;
}

export interface OrderItem {
  id: number;
  productId: number;
  quantity: number;
  product?: Product;
}

export type OrderStatus = 'PendingApproval' | 'Confirmed' | 'Expired' | 'Returned' | 'Cancelled';

export type OrderRole = 'renter' | 'lender';

export interface Order {
  id: number;
  orderDate: string;
  rentalStartDate: string;
  rentalEndDate: string;
  totalAmount: number;
  stripeSessionId?: string;
  stripePaymentIntentId?: string;
  status: number;
  handoverMessage?: string;
  renterNotes?: string;
  rentalResponseDeadline?: string;
  role?: OrderRole;
  items: OrderItem[];
}

export interface OrderContactInfo {
  trueName?: string;
  email?: string;
  phone?: string;
  approximateLatitude?: number;
  approximateLongitude?: number;
  preciseLatitude?: number;
  preciseLongitude?: number;
  preciseAddress?: string;
}

export interface OrderDashboard {
  id: number;
  orderDate: string;
  status: number;
  rentalStartDate: string;
  rentalEndDate: string;
  totalAmount: number;
  rentalResponseDeadline: string;
  handoverMessage?: string;
  renterNotes?: string;
  lenderRating?: number;
  renterRating?: number;
  role: OrderRole;
  customerName?: string;
  lenderName?: string;
  ownerContact?: OrderContactInfo;
  customerContact?: OrderContactInfo;
  productName?: string;
  items: OrderItem[];
}

export type ConditionReportRole = 'renter' | 'lender';

export interface ConditionReport {
  id: number;
  side: number; // 0 = Pickup, 1 = Return
  content?: string;
  damageReported: boolean;
  draftedByRole: ConditionReportRole;
  draftedAt: string;
  confirmedByRole?: ConditionReportRole;
  confirmedAt?: string;
  superseded: boolean;
}

export interface ConditionReportPhoto {
  id: number;
  side: number; // 0 = Pickup, 1 = Return
  uploadedByRole: ConditionReportRole;
  uploadedAt: string;
}

export interface RentalPendingOrder {
  id: number;
  status: number;
  rentalStartDate: string;
  rentalEndDate: string;
  totalAmount: number;
  rentalResponseDeadline: string;
  handoverMessage?: string;
  renterNotes?: string;
  customerName: string;
  productName: string;
  ownerContact?: OrderContactInfo;
}

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export const API_URL = '/api';

export const getErrorResponseData = (err: unknown): unknown => {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    return (err as { response?: { data?: unknown } }).response?.data;
  }
  return undefined;
};

export const getErrorMessage = (err: unknown, fallback: string): string => {
  const data = getErrorResponseData(err);
  if (typeof data === 'string' && data.length > 0) return data;
  if (typeof data === 'object' && data !== null) {
    const obj = data as { detail?: string; message?: string; title?: string; error?: string };
    if (typeof obj.detail === 'string' && obj.detail.length > 0) return obj.detail;
    if (typeof obj.message === 'string' && obj.message.length > 0) return obj.message;
    if (typeof obj.title === 'string' && obj.title.length > 0) return obj.title;
    if (typeof obj.error === 'string' && obj.error.length > 0) return obj.error;
  }
  return fallback;
};

export const BOOST_ERROR = {
  SELF: 'boost_self',
  NO_AMOUNT: 'boost_no_amount',
  NOT_FOUND: 'boost_user_not_found',
} as const;

export const getBoostErrorMessage = (err: unknown, t: (key: string) => string): string => {
  const data = getErrorResponseData(err);
  const code = typeof data === 'string'
    ? data
    : typeof data === 'object' && data !== null && 'error' in data
      ? (data as { error?: string }).error
      : undefined;

  switch (code) {
    case BOOST_ERROR.SELF:
      return t('account.boost_error_self');
    case BOOST_ERROR.NO_AMOUNT:
      return t('account.boost_error_no_amount');
    case BOOST_ERROR.NOT_FOUND:
      return t('account.boost_error_not_found');
    default:
      return getErrorMessage(err, t('account.boost_error'));
  }
};

export const CLOSED_BETA_REGISTRATION_ERROR = 'closed_beta';

export interface ClosedBetaRegistrationDetails {
  email?: string;
  firstName?: string;
  lastName?: string;
}

export const isClosedBetaRegistrationError = (err: unknown): boolean => {
  const data = getErrorResponseData(err);
  if (typeof data === 'string') return data === CLOSED_BETA_REGISTRATION_ERROR;
  if (typeof data === 'object' && data !== null && 'error' in data) {
    return (data as { error?: string }).error === CLOSED_BETA_REGISTRATION_ERROR;
  }
  return false;
};

export const getClosedBetaRegistrationDetails = (err: unknown): ClosedBetaRegistrationDetails => {
  const data = getErrorResponseData(err);
  if (typeof data === 'object' && data !== null) {
    const details = data as ClosedBetaRegistrationDetails & { error?: string };
    return {
      email: details.email,
      firstName: details.firstName,
      lastName: details.lastName,
    };
  }
  return {};
};